// src/services/documentProcessingService.js
// cspell:ignore sauvignon prosecco cava uncategorized
import mammoth from "mammoth";
import * as pdfjsLib from "pdfjs-dist";

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export class DocumentProcessingService {
  constructor() {
    // Common patterns for menu item extraction
    this.patterns = {
      // Price patterns: $12.99, $12, 12.99, etc.
      price: /\$?\d+(?:\.\d{2})?/g,

      // Section headers (usually in caps or with special formatting)
      sectionHeader: /^[A-Z\s&]+$/,

      // Wine patterns with bin numbers
      wineBin: /^(\d{1,4})\s+(.+)/,

      // Common menu item separators
      separator: /[.\-_]{3,}/,

      // Description patterns (text in parentheses or after dashes)
      description: /[(-]([^)\n]+)\)/,

      // Alcohol content patterns
      alcoholContent: /(\d+(?:\.\d+)?)\s*%/,

      // Vintage patterns
      vintage: /(19|20)\d{2}/,
    };

    // Menu categories mapping
    this.categoryKeywords = {
      "Wines by the Glass": ["by the glass", "glass wine", "house wine"],
      "Wines by the Bottle": ["by the bottle", "bottle wine", "bottles"],
      "Red Wines": ["red wine", "reds", "cabernet", "merlot", "pinot noir"],
      "White Wines": [
        "white wine",
        "whites",
        "chardonnay",
        "sauvignon blanc",
        "riesling",
      ],
      Sparkling: ["sparkling", "champagne", "prosecco", "cava"],
      Spirits: [
        "spirits",
        "whiskey",
        "vodka",
        "gin",
        "rum",
        "tequila",
        "bourbon",
      ],
      Beers: ["beer", "ales", "lager", "stout", "ipa"],
      Cocktails: ["cocktails", "mixed drinks", "specialty drinks"],
      Appetizers: ["appetizers", "starters", "small plates"],
      Entrees: ["entrees", "main course", "mains"],
      Desserts: ["desserts", "sweets"],
    };
  }

  // Process PDF document
  async processPDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = "";

      // Extract text from all pages
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map((item) => item.str).join(" ");
        fullText += pageText + "\n";
      }

      return this.parseMenuText(fullText);
    } catch (error) {
      console.error("Error processing PDF:", error);
      throw new Error("Failed to process PDF document");
    }
  }

  // Process Word document
  async processWordDocument(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });

      if (result.messages.length > 0) {
        console.warn("Word document processing warnings:", result.messages);
      }

      return this.parseMenuText(result.value);
    } catch (error) {
      console.error("Error processing Word document:", error);
      throw new Error("Failed to process Word document");
    }
  }

  // Parse extracted text into menu items
  parseMenuText(text) {
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
    const menuItems = [];
    let currentCategory = "Uncategorized";
    let currentSubcategory = null;

    for (const line of lines) {
      // Skip empty lines and separators
      if (!line || this.patterns.separator.test(line)) {
        continue;
      }

      // Check if this line is a category header
      const detectedCategory = this.detectCategory(line);
      if (detectedCategory) {
        currentCategory = detectedCategory;
        currentSubcategory = null;
        continue;
      }

      // Check if this line is a subcategory
      if (this.isSubcategory(line)) {
        currentSubcategory = line;
        continue;
      }

      // Try to parse as menu item
      const menuItem = this.parseMenuItem(
        line,
        currentCategory,
        currentSubcategory
      );
      if (menuItem) {
        menuItems.push(menuItem);
      }
    }

    return {
      items: menuItems,
      statistics: this.generateStatistics(menuItems),
      suggestions: this.generateSuggestions(menuItems),
    };
  }

  // Detect category from line text
  detectCategory(line) {
    const upperLine = line.toUpperCase();

    for (const [category, keywords] of Object.entries(this.categoryKeywords)) {
      for (const keyword of keywords) {
        if (upperLine.includes(keyword.toUpperCase())) {
          return category;
        }
      }
    }

    // Check if line looks like a header (all caps, short)
    if (this.patterns.sectionHeader.test(line) && line.length < 50) {
      return line;
    }

    return null;
  }

  // Check if line is a subcategory
  isSubcategory(line) {
    // Subcategories are usually shorter and don't contain prices
    return (
      line.length < 30 &&
      !this.patterns.price.test(line) &&
      !line.includes("$") &&
      line.split(" ").length <= 4
    );
  }

  // Parse individual menu item
  parseMenuItem(line, category, subcategory) {
    // Extract price
    const priceMatches = line.match(this.patterns.price);
    const price = priceMatches
      ? parseFloat(priceMatches[priceMatches.length - 1].replace("$", ""))
      : 0;

    // Extract wine bin number if applicable
    const binMatch = line.match(this.patterns.wineBin);
    let name = line;
    let bin = null;

    if (binMatch && category.toLowerCase().includes("wine")) {
      bin = binMatch[1];
      name = binMatch[2];
    }

    // Remove price from name
    if (priceMatches) {
      name = name.replace(this.patterns.price, "").trim();
    }

    // Extract description (text in parentheses or after dash)
    let description = "";
    const descMatch = name.match(this.patterns.description);
    if (descMatch) {
      description = descMatch[1].trim();
      name = name.replace(this.patterns.description, "").trim();
    }

    // Extract vintage for wines
    let vintage = null;
    const vintageMatch = name.match(this.patterns.vintage);
    if (
      vintageMatch &&
      (category.toLowerCase().includes("wine") ||
        category.toLowerCase().includes("spirit"))
    ) {
      vintage = parseInt(vintageMatch[0]);
    }

    // Extract alcohol content
    let alcoholContent = null;
    const alcoholMatch = line.match(this.patterns.alcoholContent);
    if (alcoholMatch) {
      alcoholContent = parseFloat(alcoholMatch[1]);
    }

    // Clean up name
    name = name.replace(/\s+/g, " ").trim();

    // Validate that this looks like a menu item
    if (name.length < 3 || name.length > 200) {
      return null;
    }

    return {
      name,
      description,
      price,
      category,
      subcategory,
      bin,
      vintage,
      alcoholContent,
      inStock: true,
      type: "item",
      source: "document_upload",
      confidence: this.calculateConfidence(name, price, category),
    };
  }

  // Calculate confidence score for parsed item
  calculateConfidence(name, price, category) {
    let confidence = 0.5; // Base confidence

    // Higher confidence if we found a price
    if (price > 0) confidence += 0.3;

    // Higher confidence if name looks reasonable
    if (name.length > 5 && name.length < 100) confidence += 0.1;

    // Higher confidence if category was detected
    if (category !== "Uncategorized") confidence += 0.1;

    return Math.min(confidence, 1.0);
  }

  // Generate statistics about parsed items
  generateStatistics(items) {
    const categoryCount = {};
    let totalWithPrices = 0;
    let priceSum = 0;
    let highConfidenceItems = 0;

    items.forEach((item) => {
      // Count by category
      categoryCount[item.category] = (categoryCount[item.category] || 0) + 1;

      // Price statistics
      if (item.price > 0) {
        totalWithPrices++;
        priceSum += item.price;
      }

      // Confidence statistics
      if (item.confidence > 0.7) {
        highConfidenceItems++;
      }
    });

    return {
      totalItems: items.length,
      categoriesFound: Object.keys(categoryCount).length,
      categoryBreakdown: categoryCount,
      itemsWithPrices: totalWithPrices,
      averagePrice:
        totalWithPrices > 0 ? (priceSum / totalWithPrices).toFixed(2) : 0,
      highConfidenceItems,
      confidencePercentage:
        items.length > 0
          ? ((highConfidenceItems / items.length) * 100).toFixed(1)
          : 0,
    };
  }

  // Generate suggestions for improvement
  generateSuggestions(items) {
    const suggestions = [];

    // Check for items without prices
    const itemsWithoutPrices = items.filter((item) => item.price === 0).length;
    if (itemsWithoutPrices > 0) {
      suggestions.push({
        type: "warning",
        message: `${itemsWithoutPrices} items found without prices. You may need to add prices manually.`,
      });
    }

    // Check for low confidence items
    const lowConfidenceItems = items.filter(
      (item) => item.confidence < 0.5
    ).length;
    if (lowConfidenceItems > 0) {
      suggestions.push({
        type: "info",
        message: `${lowConfidenceItems} items have low confidence scores. Please review these items carefully.`,
      });
    }

    // Check for uncategorized items
    const uncategorizedItems = items.filter(
      (item) => item.category === "Uncategorized"
    ).length;
    if (uncategorizedItems > 0) {
      suggestions.push({
        type: "info",
        message: `${uncategorizedItems} items couldn't be categorized automatically. Please assign categories manually.`,
      });
    }

    return suggestions;
  }

  // Validate and clean parsed data
  validateAndClean(parsedData) {
    const cleanedItems = parsedData.items.map((item) => ({
      ...item,
      name: this.cleanText(item.name),
      description: this.cleanText(item.description),
      category: item.category || "Uncategorized",
      price: Math.max(0, item.price || 0),
      inStock: true,
    }));

    return {
      ...parsedData,
      items: cleanedItems,
    };
  }

  // Clean and normalize text
  cleanText(text) {
    if (!text) return "";

    return text
      .replace(/\s+/g, " ") // Replace multiple spaces with single space
      .replace(/[^\w\s\-().,$%]/g, "") // Remove special characters except common ones
      .trim();
  }

  /**
   * PHASE 2 INTEGRATION METHODS
   * Enhanced methods for AI-powered document processing
   */

  /**
   * Process document with AI enhancement
   */
  async processDocumentWithAI(file, options = {}) {
    try {
      // First, extract basic content
      const basicProcessing = await this.processDocument(file);

      if (!basicProcessing.success) {
        return basicProcessing;
      }

      // Import AI services dynamically to avoid circular dependencies
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );
      await masterOrchestrator.initialize();

      // Use AI to enhance menu parsing
      const aiEnhancedMenu = await this.enhanceMenuWithAI(
        basicProcessing.items,
        options
      );

      return {
        ...basicProcessing,
        aiEnhanced: true,
        enhancedItems: aiEnhancedMenu,
        metadata: {
          ...basicProcessing.metadata,
          aiProcessed: true,
          enhancementTimestamp: new Date().toISOString(),
        },
      };
    } catch (error) {
      console.error("Error in AI-enhanced document processing:", error);

      // Fallback to basic processing
      return this.processDocument(file);
    }
  }

  /**
   * Enhance parsed menu items with AI
   */
  async enhanceMenuWithAI(menuItems, options = {}) {
    try {
      const { masterOrchestrator } = await import(
        "./masterServiceOrchestrator.js"
      );
      const enhancedItems = [];

      for (const item of menuItems) {
        try {
          // Use master orchestrator for beverage enhancement
          const enhanced = await masterOrchestrator.enhanceBeverage(item, {
            ...options,
            skipExternalApi: true, // Avoid external API calls during bulk processing
            enhanceDescription: true,
          });

          enhancedItems.push({
            ...item,
            aiEnhanced: enhanced,
            confidence: (item.confidence || 50) + 20, // Boost confidence for AI-enhanced items
          });
        } catch (error) {
          console.warn(`Failed to AI-enhance item ${item.name}:`, error);
          enhancedItems.push(item);
        }

        // Add delay to avoid rate limiting
        if (options.delay && options.delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, options.delay));
        }
      }

      return enhancedItems;
    } catch (error) {
      console.error("Error in AI menu enhancement:", error);
      return menuItems;
    }
  }

  /**
   * Parse food menu for pairing opportunities
   */
  async parseFoodMenuForPairings(file, options = {}) {
    try {
      const processed = await this.processDocument(file);

      if (!processed.success) {
        return processed;
      }

      // Import intelligent pairing system
      const { IntelligentPairingSystem } = await import(
        "./intelligentPairingSystem.js"
      );
      const pairingSystem = new IntelligentPairingSystem();

      // Parse and analyze the menu for pairing opportunities
      const pairingAnalysis = await pairingSystem.parseAndAnalyzeMenu(
        processed.text,
        options
      );

      return {
        ...processed,
        pairingAnalysis,
        foodMenuParsed: true,
        metadata: {
          ...processed.metadata,
          pairingOpportunities: pairingAnalysis.pairings?.length || 0,
        },
      };
    } catch (error) {
      console.error("Error parsing food menu for pairings:", error);
      throw error;
    }
  }

  /**
   * Batch process multiple documents
   */
  async batchProcessDocuments(files, options = {}) {
    const results = {
      total: files.length,
      successful: 0,
      failed: 0,
      results: [],
    };

    for (const file of files) {
      try {
        const result = options.useAI
          ? await this.processDocumentWithAI(file, options)
          : await this.processDocument(file);

        results.results.push({
          fileName: file.name,
          success: result.success,
          data: result,
        });

        if (result.success) {
          results.successful++;
        } else {
          results.failed++;
        }
      } catch (error) {
        results.results.push({
          fileName: file.name,
          success: false,
          error: error.message,
        });
        results.failed++;
      }

      // Add delay between files
      if (options.delay && options.delay > 0) {
        await new Promise((resolve) => setTimeout(resolve, options.delay));
      }
    }

    return results;
  }

  /**
   * Smart category detection using AI patterns
   */
  async detectCategoriesWithAI(text, options = {}) {
    try {
      // Use basic pattern matching first
      const basicCategories = this.detectCategories(text);

      // Import AI service for enhanced categorization
      const { callAnthropicAI } = await import("./aiService.js");

      const prompt = `Analyze this menu text and identify beverage categories. Return a JSON array of category names found:

Text: ${text.substring(0, 2000)}...

Please identify categories like:
- Wine types (Red Wines, White Wines, Sparkling, etc.)
- Beer categories (IPAs, Lagers, Stouts, etc.)
- Spirits (Whiskeys, Vodkas, Gins, etc.)
- Cocktails
- Non-alcoholic beverages

Return only a JSON array of category names, no other text.`;

      const aiResponse = await callAnthropicAI(prompt);
      const aiCategories = JSON.parse(aiResponse);

      // Combine basic and AI-detected categories
      const combinedCategories = [
        ...new Set([...basicCategories, ...aiCategories]),
      ];

      return combinedCategories;
    } catch (error) {
      console.warn(
        "AI category detection failed, using basic detection:",
        error
      );
      return this.detectCategories(text);
    }
  }

  /**
   * Validate and clean processed menu data
   */
  validateMenuData(menuData, options = {}) {
    const validation = {
      valid: true,
      warnings: [],
      errors: [],
      suggestions: [],
    };

    // Check for minimum viable menu structure
    if (!menuData.items || menuData.items.length === 0) {
      validation.errors.push("No menu items found");
      validation.valid = false;
    }

    // Check for price information
    const itemsWithPrices = menuData.items.filter(
      (item) => item.price && item.price > 0
    );
    if (itemsWithPrices.length < menuData.items.length * 0.5) {
      validation.warnings.push(
        `Only ${itemsWithPrices.length}/${menuData.items.length} items have pricing information`
      );
    }

    // Check for descriptions
    const itemsWithDescriptions = menuData.items.filter(
      (item) => item.description && item.description.length > 10
    );
    if (itemsWithDescriptions.length < menuData.items.length * 0.3) {
      validation.suggestions.push(
        "Consider adding more detailed descriptions to menu items"
      );
    }

    // Check for categories
    const itemsWithCategories = menuData.items.filter((item) => item.category);
    if (itemsWithCategories.length < menuData.items.length * 0.8) {
      validation.suggestions.push(
        "Consider organizing items into clear categories"
      );
    }

    return validation;
  }

  /**
   * Export processed menu in various formats
   */
  exportProcessedMenu(menuData, format = "json") {
    switch (format.toLowerCase()) {
      case "json":
        return JSON.stringify(menuData, null, 2);

      case "csv":
        return this.convertMenuToCSV(menuData.items);

      case "firestore":
        return this.formatForFirestore(menuData.items);

      default:
        throw new Error(`Unsupported export format: ${format}`);
    }
  }

  /**
   * Convert menu items to CSV format
   */
  convertMenuToCSV(items) {
    if (!items || items.length === 0) return "";

    const headers = ["name", "category", "price", "description", "type"];
    const csvRows = [headers.join(",")];

    items.forEach((item) => {
      const row = headers.map((header) => {
        const value = item[header] || "";
        return `"${String(value).replace(/"/g, '""')}"`;
      });
      csvRows.push(row.join(","));
    });

    return csvRows.join("\n");
  }

  /**
   * Format menu items for Firestore import
   */
  formatForFirestore(items) {
    return items.map((item) => ({
      name: item.name || "",
      category: item.category || "uncategorized",
      price: item.price || 0,
      description: item.description || "",
      type: item.type || "beverage",
      source: "document_processing",
      imported: new Date().toISOString(),
      needsReview: !item.price || !item.category,
    }));
  }

  /**
   * Get processing statistics
   */
  getProcessingStats() {
    return {
      supportedFormats: ["pdf", "docx", "txt", "image"],
      processingCapabilities: [
        "text_extraction",
        "menu_parsing",
        "category_detection",
        "price_extraction",
        "ai_enhancement",
        "batch_processing",
      ],
      aiFeatures: [
        "intelligent_categorization",
        "beverage_enhancement",
        "pairing_analysis",
        "quality_validation",
      ],
    };
  }
}

// Create singleton instance
export const documentProcessor = new DocumentProcessingService();
